import React from 'react';
import { Modal, Button } from 'react-bootstrap';
import './CustomModal.css';

/**
 * CustomModal - A reusable modal component for the application
 * 
 * @param {boolean} show - Controls modal visibility
 * @param {function} onHide - Callback when modal is closed
 * @param {string} title - Modal title
 * @param {node} children - Modal body content
 * @param {string} size - Modal size ('sm', 'md', 'lg', 'xl')
 * @param {string} className - Additional CSS classes
 * @param {boolean} centered - Center modal vertically
 * @param {boolean} closeButton - Show close button in header
 * @param {node} customHeader - Custom header content (overrides title)
 * @param {array} footerButtons - Array of button configurations
 * @param {boolean} showFooter - Controls whether to show the footer buttons (default: true)
 * @param {boolean} closeOnBackdrop - Close modal when clicking backdrop
 * @param {function} onEntered - Callback when modal is fully shown
 * @param {function} onExited - Callback when modal is fully hidden
 */
function CustomModal({
  show,
  onHide,
  title,
  children,
  size = 'md',
  className = '',
  centered = true,
  closeButton = true,
  customHeader = null,
  footerButtons = [],
  showFooter = true,
  closeOnBackdrop = true,
  onEntered = null,
  onExited = null,
  ...rest
}) {
  // Default footer buttons if none provided
  const defaultFooterButtons = footerButtons.length === 0 
    ? [
        {
          label: 'Close',
          variant: 'secondary',
          onClick: onHide,
          disabled: false
        }
      ]
    : footerButtons;

  return (
    <Modal
      show={show}
      onHide={onHide}
      size={size}
      centered={centered}
      className={`custom-modal ${className}`}
      backdrop={closeOnBackdrop ? true : 'static'}
      onEntered={onEntered}
      onExited={onExited}
      {...rest}
    >
      <Modal.Header closeButton={closeButton}>
        {customHeader || (
          <Modal.Title>{title}</Modal.Title>
        )}
      </Modal.Header>
      <Modal.Body>
        {children}
      </Modal.Body>
      {showFooter && defaultFooterButtons.length > 0 && (
        <Modal.Footer>
          {defaultFooterButtons.map((button, index) => (
            <Button
              key={index}
              variant={button.variant || 'secondary'}
              onClick={button.onClick}
              disabled={button.disabled || false}
              className={button.className || ''}
            >
              {button.loading && (
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
              )}
              {button.icon && <span className="me-2">{button.icon}</span>}
              {button.label}
            </Button>
          ))}
        </Modal.Footer>
      )}
    </Modal>
  );
}

export default CustomModal;

